import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import navlogo from "../assets/navlogo.png";
import AppointmentModal from "../components/modals/AppointmentModal";

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: 'HOME', href: '/', isRoute: true },
    { name: 'OUR SERVICES', href: '/services', isRoute: true },
    // { name: 'OUR PROCESS', href: '/process', isRoute: true },
    { name: 'ABOUT US', href: '/about', isRoute: true },
    { name: 'CONTACT US', href: '#contact', isContact: true }
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen || isModalOpen ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen, isModalOpen]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const openModal = () => {
    setIsModalOpen(true);
    setIsMobileMenuOpen(false);
  };
  const closeModal = () => setIsModalOpen(false);

  const handleNavClick = (href: string, isContact?: boolean, isRoute?: boolean) => {
    if (isContact) {
      openModal();
      return;
    }
    setIsMobileMenuOpen(false);
    if (isRoute) {
      // Let React Router handle the navigation
      return;
    }
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
        return;
      }
      if (location.pathname !== '/') {
        navigate({ pathname: '/', hash: href });
      }
    }
  };

  return (
    <>
      <nav
        className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/90 backdrop-blur-md shadow-sm' : 'bg-white/80'
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img
                  src={navlogo}
                  alt="AWM Makeovers by Aastha Logo"
                  className="h-32 sm:h-32 md:h-32 lg:h-36 w-auto object-contain transition-transform duration-200 hover:scale-105"
                />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-6">
              {navItems.map((item) => {
                const isActive = location.pathname === item.href;
                
                if (item.isRoute) {
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`relative px-2 py-1 text-md font-normal tracking-wide transition-all duration-200 group font-body ${
                        isActive ? 'text-black' : 'text-gray-600 hover:text-black'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.name}
                      <span
                        className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#622935] transform origin-left transition-transform duration-200 ${
                          isActive
                            ? 'scale-x-100'
                            : 'scale-x-0 group-hover:scale-x-100'
                        }`}
                      />
                    </Link>
                  );
                } else {
                  return (
                    <a
                      key={item.name}
                      href={item.href}
                      onClick={(e: React.MouseEvent) => {
                        e.preventDefault();
                        handleNavClick(item.href, item.isContact, item.isRoute);
                      }}
                      className={`relative px-2 py-1 text-md font-normal tracking-wide transition-all duration-200 group font-body ${
                        isActive ? 'text-black' : 'text-gray-600 hover:text-black'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {item.name}
                      <span
                        className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#622935] transform origin-left transition-transform duration-200 ${
                          isActive
                            ? 'scale-x-100'
                            : 'scale-x-0 group-hover:scale-x-100'
                        }`}
                      />
                    </a>
                  );
                }
              })}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="p-2 rounded-md text-gray-600 hover:text-black hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#622935] transition-colors"
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6" aria-hidden="true" />
                ) : (
                  <Menu className="w-6 h-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
            isMobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="px-4 pt-4 pb-6 space-y-1 bg-white border-t border-gray-300 shadow-lg">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;
              
              if (item.isRoute) {
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-3 py-2 text-base font-normal rounded-md transition-colors font-body ${
                      isActive
                        ? 'text-black bg-gray-100 border-l-4 border-[#622935]'
                        : 'text-gray-600 hover:text-black hover:bg-gray-100'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.name}
                  </Link>
                );
              } else {
                return (
                  <a
                    key={item.name}
                    href={item.href}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      handleNavClick(item.href, item.isContact, item.isRoute);
                    }}
                    className={`block px-3 py-2 text-base font-normal rounded-md transition-colors font-body ${
                      isActive
                        ? 'text-black bg-gray-100 border-l-4 border-[#622935]'
                        : 'text-gray-600 hover:text-black hover:bg-gray-100'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    {item.name}
                  </a>
                );
              }
            })}
            {/* CTA Removed or Neutral */}
            <div className="pt-4">
              <button
                onClick={openModal}
                className="w-full bg-[#622935] text-white px-4 py-3 rounded-md font-medium hover:bg-[#4A1F28] transition-colors focus:outline-none focus:ring-2 focus:ring-[#622935] focus:ring-offset-2 font-body"
              >
                BOOK NOW
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Appointment Modal */}
      <AppointmentModal isOpen={isModalOpen} onClose={closeModal} />
    </>
  );
};

export default Navbar;
